'use client';
import { useParams } from 'next/navigation';
import { memo, useRef } from 'react';

import { Text } from '~components/Typography';
import { routes } from '~constants/routes';
import { useReportActionsContext } from '~context/report';
import { useFormatBotStatistics } from '~entities/bot/hooks/useFormatBotStatistics';
import { annals } from '~features/annals/hooks';
import { useResetMemoryClicked } from '~features/annals/hooks/chat';
import { ResetMemoryModal } from '~features/resetMemory/ui/ResetMemoryModal';
import { Toaster } from '~features/toaster';
import { useCurrentBot } from '~hooks/useCurrentBot';
import { useModal } from '~hooks/useModal';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import { Link } from '~i18n/navigation';
import FlagIcon from '~public/assets/icons/flag.svg';
import GearIcon from '~public/assets/icons/gear.svg';
import LinkIcon from '~public/assets/icons/link.svg';
import ResetIcon from '~public/assets/icons/reset.svg';
import ProfileIcon from '~public/assets/icons/user-circle.svg';
import DiscordIcon from '~public/img/social-networks/discord-icon.svg';
import { botsApi } from '~services/api/bots';
import { Dropdown, DropdownElement, ToggleSwitch } from '~shared/ui/atoms';
import { ButtonIconSecondary } from '~shared/ui/molecules';

import { styledDropdownElement, StyledPExplore, Wrapper } from './styles';

export const BotActions = memo(() => {
	const { userId, isDemoUser } = useSession();
	const dropdownRef = useRef<HTMLButtonElement>(null);
	const t = useTranslations('BotActions');

	const sendResetMemoryClickedEvent = useResetMemoryClicked();
	const { reportCharacter } = useReportActionsContext();
	const { recipientId = '' } = useParams<{ recipientId?: string }>();
	const { isOpen, open, close } = useModal();
	const [changeBotAccess] = botsApi.useChangeBotAccessMutation();
	const { bot } = useCurrentBot({
		recipientId,
	});
	const { data } = botsApi.useGetBotAccessInfoQuery(
		{ userId, recipientId },
		{ skip: !recipientId || isDemoUser || !bot.clientId }
	);

	const ugcBotStatusChanged = annals.bot.useUGCBotStatusChanged({
		botName: bot?.name || '',
		botId: bot?.clientId?.toString() || '',
	});
	const ugcBotLinkCopied = annals.bot.useUGCBotLinkCopied({
		botName: bot?.name || '',
		status: bot?.access || 'public',
		botId: bot?.clientId?.toString() || '',
	});

	const ugcBotOptionsOpen = annals.bot.useUGCBotOptionsOpen({
		botName: bot?.name || '',
		status: bot?.access || 'public',
		botId: bot?.clientId?.toString() || '',
		messagesCount:
			bot.statistics && bot?.access === 'public'
				? bot.statistics.botCount + bot.statistics.userCount
				: undefined,
	});
	const shareWithDiscordButtonClick = annals.bot.useUGCBotShareDiscordLinkClick(
		{
			botName: bot?.name || '',
			status: bot?.access || 'public',
			botId: bot?.clientId?.toString() || '',
		}
	);

	const handleChangeBotAccess = async () => {
		const newAccess = bot.access === 'public' ? 'private' : 'public';
		await changeBotAccess({
			userId,
			recipientId: bot.clientId.toString(),
			access: newAccess,
		});
		ugcBotStatusChanged({ bot_status: newAccess });
	};

	const handleCopy = async () => {
		try {
			await navigator?.clipboard?.writeText(window.location.href);
			ugcBotLinkCopied();
			Toaster.showSuccess({
				titleKey: 'copyLinkToChatSuccessTitle',
				textKey: 'copyLinkToChatSuccessMessage',
			});
		} catch (error) {
			Toaster.showError({
				titleKey: 'copyLinkToChatErrorTitle',
				textKey: 'copyLinkToChatErrorMessage',
			});
		}
	};

	const openDiscord = async () => {
		try {
			await navigator?.clipboard?.writeText(window.location.href);
			window.open('https://discord.gg/eUbmtE7QJp', '_blank');
			shareWithDiscordButtonClick();
		} catch (error) {
			Toaster.showError({
				titleKey: 'shareWithDiscordErrorTitle',
				textKey: 'shareWithDiscordErrorMessage',
			});
		}
	};

	const handleResetClick = () => {
		open()();
		sendResetMemoryClickedEvent({
			bot_UserId: bot.clientId,
		});
	};

	const logOpenBotProfile = annals.botProfile.useBotProfileOpened({
		source: 'menu',
		botClientId: bot?.clientId?.toString(),
	});

	const onOpenBotProfileClicked = () => {
		logOpenBotProfile();
	};

	const totalMessagesCount = useFormatBotStatistics(bot.statistics, true);

	return (
		<>
			<ResetMemoryModal open={isOpen} onClose={close} />
			<ButtonIconSecondary
				type="button"
				bigIcon={true}
				leftIcon={<GearIcon />}
				ref={dropdownRef}
			/>
			<Dropdown
				placement="bottom-end"
				targetRef={dropdownRef}
				onChange={(showed) => {
					if (showed) {
						ugcBotOptionsOpen();
					}
				}}
			>
				<DropdownElement
					onClick={onOpenBotProfileClicked}
					as={Link}
					href={routes.profile.slug(recipientId)}
				>
					<ProfileIcon width={22} height={22} />
					{t('openProfileTitle')}
				</DropdownElement>
				{bot?.accountType === 'user-generated' && data?.isOwner && (
					<DropdownElement
						className={styledDropdownElement}
						closeOnClick={false}
						onClick={handleChangeBotAccess}
					>
						<Wrapper>
							<ToggleSwitch
								color="var(--color-muted)"
								activeColor="var(--color-error)"
								height={10}
								thumbSize={14}
								width={26}
								checked={bot.access === 'public'}
							/>
							<StyledPExplore size="M">
								{bot.access === 'public'
									? t('availableForExploreToggle')
									: t('unavailableForExploreToggle')}
							</StyledPExplore>
						</Wrapper>
						{bot.access === 'public' &&
							bot.accountType === 'user-generated' && (
								<Text size="S" color="semi-transparent">
									{totalMessagesCount}
								</Text>
							)}
					</DropdownElement>
				)}
				{bot.access === 'public' && (
					<>
						<DropdownElement onClick={openDiscord}>
							<DiscordIcon width={22} height={22} />
							<Text size="M" data-name="share-with-discord">
								{t('shareWithDiscordTitle')}
							</Text>
						</DropdownElement>
						<DropdownElement onClick={handleCopy}>
							<LinkIcon width={22} height={22} />
							<Text size="M" data-name="copy-link-to-chat">
								{t('copyLinkToChatTitle')}
							</Text>
						</DropdownElement>
					</>
				)}
				{bot?.accountType === 'user-generated' && !isDemoUser && (
					<DropdownElement
						onClick={reportCharacter({
							botId: bot.clientId,
						})}
					>
						<FlagIcon width={22} height={22} />
						<Text size="M" color="warn" data-name="report-item-title">
							{t('reportItemTitle')}
						</Text>
					</DropdownElement>
				)}

				<DropdownElement onClick={handleResetClick}>
					<ResetIcon width={22} height={22} />
					<Text size="M" color="warn" data-name="reset-memory-item-title">
						{t('resetMemoryItemTitle')}
					</Text>
				</DropdownElement>
			</Dropdown>
		</>
	);
});

BotActions.displayName = 'BotActions';
